import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useArtistStore } from '../../../hooks/useArtistStore';
import { usePrefectureArtists } from '../../../hooks/usePrefectureArtists';
import { FavoriteButton } from '../../../components/feature/FavoriteButton';
import { useArtistGenreColors, findGenreColor } from '../../../hooks/useGenreColors';

interface ArtistListProps {
  prefectureId: string;
}

/**
 * ArtistList component
 *
 * Displays a horizontal scrollable list of artists for a given prefecture.
 * It combines real DB artists (fetched via the custom hook) with mock
 * artists from a local JSON file. The component safely handles loading
 * states, empty results and includes basic defensive programming to
 * avoid runtime crashes.
 */
export function ArtistList({ prefectureId }: ArtistListProps) {
  const navigate = useNavigate();
  const { artists: allMockArtists } = useArtistStore();
  const genreColorMap = useArtistGenreColors();

  // Fetch artists from the backend (could be loading, error or data)
  const { artists: dbArtists = [], loading: dbLoading } = usePrefectureArtists(prefectureId);

  // Filter mock artists that belong to the same prefecture
  const mockArtists = React.useMemo(
    () => allMockArtists.filter((a) => a.prefecture === prefectureId),
    [prefectureId, allMockArtists]
  );

  // Total number of artists (DB + mock)
  const totalCount = dbArtists.length + mockArtists.length;

  // If we are not loading and there is nothing to display, render nothing
  if (!dbLoading && totalCount === 0) return null;

  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-2 h-2 rounded-full bg-neon-pink"
            style={{ boxShadow: '0 0 10px #ff006e' }}
          />
          <h3 className="text-white font-display text-lg sm:text-xl tracking-wider">
            ARTISTS
          </h3>
          <span className="text-gray-600 text-xs tracking-wider">
            / 登録アーティスト
          </span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-neon-pink/10 border border-neon-pink/20">
          <i className="ri-user-star-line text-neon-pink text-xs" />
          <span className="text-neon-pink text-[10px] font-bold tracking-wider">
            {dbLoading ? '...' : `${totalCount} ARTISTS`}
          </span>
        </div>
      </div>

      {/* Loading state */}
      {dbLoading && (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line text-2xl text-neon-pink animate-spin" />
        </div>
      )}

      {/* Horizontal Scroll Container */}
      {!dbLoading && (
        <div className="relative">
          {/* Mobile: 1列縦並び */}
          <div className="sm:hidden flex flex-col gap-4">
            {dbArtists.map((artist) => {
              const genreColor = findGenreColor(artist.genre || '', genreColorMap, artist.color);
              return (
                <div
                  key={`db-${artist.userId}`}
                  className="w-full group cursor-pointer"
                  onClick={() => navigate(`/artist/u/${artist.userId}`)}
                >
                  <div
                    className="relative bg-[#111] rounded-xl overflow-hidden transition-all duration-300"
                    style={{ border: `1px solid ${artist.color}33` }}
                  >
                    <div className="relative w-full h-[200px] overflow-hidden">
                      {artist.image ? (
                        <img src={artist.image} alt={artist.name} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${artist.color}20, ${artist.color}05)` }}>
                          <i className="ri-user-line text-5xl" style={{ color: `${artist.color}60` }} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-neon-green/20 border border-neon-green/40">
                        <span className="text-neon-green text-[8px] font-bold tracking-wider">REGISTERED</span>
                      </div>
                      <div className="absolute top-2 right-2 z-10">
                        <FavoriteButton artistId={artist.userId} artistType="artist" size="sm" />
                      </div>
                    </div>
                    <div className="px-3 pt-2 pb-3">
                      <span className="text-[9px] tracking-widest block mb-1" style={{ color: artist.color }}>{artist.location}</span>
                      <h4 className="text-white text-sm font-bold tracking-wide truncate mb-1">{artist.name}</h4>
                      <div className="w-8 h-0.5 mb-2" style={{ background: artist.color, boxShadow: `0 0 8px ${artist.color}` }} />
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider" style={{ background: `${genreColor}15`, color: genreColor, border: `1px solid ${genreColor}30` }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: genreColor, boxShadow: `0 0 4px ${genreColor}` }} />
                        <span className="truncate max-w-[120px]">{artist.genre || 'Artist'}</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {mockArtists.map((artist) => {
              const genreColor = findGenreColor(artist.genre, genreColorMap, artist.color);
              return (
                <div
                  key={`mock-${artist.id}`}
                  className="w-full group cursor-pointer"
                  onClick={() => navigate(`/artist/${artist.id}`)}
                >
                  <div
                    className="relative bg-[#111] rounded-xl overflow-hidden transition-all duration-300"
                    style={{ border: `1px solid ${artist.color}33` }}
                  >
                    <div className="relative w-full h-[200px] overflow-hidden">
                      <img src={artist.image} alt={artist.name} className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />
                      <div className="absolute top-2 right-2 z-10">
                        <FavoriteButton artistId={String(artist.id)} artistType="mock" size="sm" />
                      </div>
                    </div>
                    <div className="px-3 pt-2 pb-3">
                      <span className="text-[9px] tracking-widest block mb-1" style={{ color: artist.color }}>{artist.location}</span>
                      <h4 className="text-white text-sm font-bold tracking-wide truncate mb-1">{artist.name}</h4>
                      <div className="w-8 h-0.5 mb-2" style={{ background: artist.color, boxShadow: `0 0 8px ${artist.color}` }} />
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider" style={{ background: `${genreColor}15`, color: genreColor, border: `1px solid ${genreColor}30` }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: genreColor, boxShadow: `0 0 4px ${genreColor}` }} />
                        <span className="truncate max-w-[120px]">{artist.genre}</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* PC: 横スクロール */}
          <div
            className="hidden sm:flex gap-4 overflow-x-auto pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {/* DB 登録アーティスト（優先表示） */}
            {dbArtists.map((artist) => {
              const genreColor = findGenreColor(artist.genre || '', genreColorMap, artist.color);
              return (
                <div
                  key={`db-${artist.userId}`}
                  className="flex-shrink-0 w-48 sm:w-56 group cursor-pointer"
                  onClick={() => navigate(`/artist/u/${artist.userId}`)}
                >
                  <div
                    className="relative bg-[#111] rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-2"
                    style={{
                      border: `1px solid ${artist.color}33`,
                      boxShadow: '0 0 0 rgba(0,0,0,0)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${artist.color}40`;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.boxShadow = '0 0 0 rgba(0,0,0,0)';
                    }}
                  >
                    <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                      {artist.image ? (
                        <img src={artist.image} alt={artist.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${artist.color}20, ${artist.color}05)` }}>
                          <i className="ri-user-line text-5xl" style={{ color: `${artist.color}60` }} />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-neon-green/20 border border-neon-green/40">
                        <span className="text-neon-green text-[8px] font-bold tracking-wider">REGISTERED</span>
                      </div>
                      <div className="absolute top-2 right-2 z-10">
                        <FavoriteButton artistId={artist.userId} artistType="artist" size="sm" />
                      </div>
                      <div className="absolute bottom-2 right-2 flex gap-1.5">
                        {artist.platforms.slice(0, 3).map((platform) => {
                          const iconMap: Record<string, string> = {
                            youtube: 'ri-youtube-line',
                            soundcloud: 'ri-soundcloud-line',
                            spotify: 'ri-spotify-line',
                            instagram: 'ri-instagram-line',
                            twitter: 'ri-twitter-x-line',
                            tiktok: 'ri-tiktok-line',
                          };
                          return (
                            <div key={platform} className="w-6 h-6 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm">
                              <i className={`${iconMap[platform] || 'ri-music-line'} text-[10px] text-white/70`} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="px-3 pt-2 pb-3">
                      <span className="text-[9px] tracking-widest block mb-1" style={{ color: artist.color }}>{artist.location}</span>
                      <h4 className="text-white text-sm font-bold tracking-wide truncate mb-1">{artist.name}</h4>
                      <div className="w-8 h-0.5 mb-2" style={{ background: artist.color, boxShadow: `0 0 8px ${artist.color}` }} />
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider" style={{ background: `${genreColor}15`, color: genreColor, border: `1px solid ${genreColor}30` }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: genreColor, boxShadow: `0 0 4px ${genreColor}` }} />
                        <span className="truncate max-w-[120px]">{artist.genre || 'Artist'}</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Mock Artists */}
            {mockArtists.map((artist) => {
              const genreColor = findGenreColor(artist.genre, genreColorMap, artist.color);
              return (
                <div
                  key={`mock-${artist.id}`}
                  className="flex-shrink-0 w-48 sm:w-56 group cursor-pointer"
                  onClick={() => navigate(`/artist/${artist.id}`)}
                >
                  <div
                    className="relative bg-[#111] rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-2"
                    style={{
                      border: `1px solid ${artist.color}33`,
                      boxShadow: '0 0 0 rgba(0,0,0,0)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${artist.color}40`;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.boxShadow = '0 0 0 rgba(0,0,0,0)';
                    }}
                  >
                    <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                      <img src={artist.image} alt={artist.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />
                      <div className="absolute top-2 right-2 z-10">
                        <FavoriteButton artistId={String(artist.id)} artistType="mock" size="sm" />
                      </div>
                      <div className="absolute bottom-2 right-2 flex gap-1.5">
                        {artist.platforms.slice(0, 3).map((platform) => {
                          const iconMap: Record<string, string> = {
                            youtube: 'ri-youtube-line',
                            soundcloud: 'ri-soundcloud-line',
                            spotify: 'ri-spotify-line',
                            instagram: 'ri-instagram-line',
                          };
                          return (
                            <div key={platform} className="w-6 h-6 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm">
                              <i className={`${iconMap[platform] || 'ri-music-line'} text-[10px] text-white/70`} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="px-3 pt-2 pb-3">
                      <span className="text-[9px] tracking-widest block mb-1" style={{ color: artist.color }}>{artist.location}</span>
                      <h4 className="text-white text-sm font-bold tracking-wide truncate mb-1">{artist.name}</h4>
                      <div className="w-8 h-0.5 mb-2" style={{ background: artist.color, boxShadow: `0 0 8px ${artist.color}` }} />
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider" style={{ background: `${genreColor}15`, color: genreColor, border: `1px solid ${genreColor}30` }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: genreColor, boxShadow: `0 0 4px ${genreColor}` }} />
                        <span className="truncate max-w-[120px]">{artist.genre}</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right fade edge (PC only) */}
          {totalCount > 0 && (
            <div className="hidden sm:flex absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-[#0a0a0a] to-transparent pointer-events-none items-center justify-end pr-2">
              <i className="ri-arrow-right-s-line text-gray-600 text-2xl" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
